import Link from "next/link";
import React, { useState, useEffect } from "react";
import styles from "@/styles/HeroImageSection.module.css";
import Announce from "../announce/Announce";
import RoleSelectionModal from "@/components/Modal/loginModal";
import LogoutModal from "@/components/Modal/LogoutModal";

const HeroImageSection = () => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLogoutSuccessModal, setShowLogoutSuccessModal] = useState(false);
  const [user, setUser] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(storedUser);
    }
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${process.env.NEXT_PUBLIC_API_URL}/logout`, {
        method: "GET",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }

    localStorage.removeItem("user");
    setUser(null);
    setShowLogoutSuccessModal(true);
    setShowConfirm(false);

    setTimeout(() => {
      setShowLogoutSuccessModal(false);
    }, 3000);
  };

  const handleLoginOrLogoutClick = (e) => {
    e.preventDefault();

    if (user) {
      setShowConfirm(true);
    } else {
      setShowLoginModal(true);
    }
  };

  const handleCancelLogout = () => {
    setShowConfirm(false);
  };

  return (
    <>
      <RoleSelectionModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        type="login"
      />
      {showConfirm && (
        <LogoutModal
          handleLogout={handleLogout}
          handleCancelLogout={handleCancelLogout}
        />
      )}

      <style jsx>{`
        .hero-main-banner {
          position: relative;
          max-height: 100%;
          overflow: hidden;
          margin-bottom: -7px;
        }

        .hero-bg-video {
          min-width: 100%;
          min-height: 100vh;
          max-width: 100%;
          max-height: 100vh;
          object-fit: cover;
          z-index: -1;
        }

        .hero-bg-video::-webkit-media-controls {
          display: none !important;
        }

        .hero-video-overlay {
          position: absolute;
          background-color: rgba(31, 39, 43, 0.75);
          top: 0;
          left: 0;
          bottom: 0;
          right: 0;
          width: 100%;
        }

        .hero-caption {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
        }

        .hero-caption h6 {
          margin-top: 0px;
          font-size: 15px;
          text-transform: uppercase;
          font-weight: 600;
          color: #fff;
          letter-spacing: 1px;
        }

        .hero-caption h2 {
          margin-top: 20px;
          margin-bottom: 20px;
          font-size: 36px;
          text-transform: uppercase;
          font-weight: 800;
          color: #fff;
          letter-spacing: 1px;
        }

        .hero-caption h2 em {
          font-style: normal;
          color: #f5a425;
          font-weight: 900;
        }

        .hero-caption p {
          color: #fff;
          font-size: 14px;
          max-width: 570px;
        }

        .hero-caption .hero-main-button-red {
          margin-top: 30px;
        }

        .hero-main-button-red a {
          font-size: 13px;
          color: #fff;
          background-color: #a12c2f;
          padding: 12px 30px;
          display: inline-block;
          border-radius: 22px;
          font-weight: 500;
          text-transform: uppercase;
          transition: all 0.3s;
          text-decoration: none;
          cursor: pointer;
        }

        .hero-main-button-red a:hover {
          opacity: 0.9;
        }

        .hero-link-yellow {
          color: rgb(21, 0, 255);
          font-weight: bold;
          text-decoration: none;
        }

        .hero-list {
          list-style: none;
          color: white;
          padding-left: 0;
        }

        .hero-cliptext {
          color: #a259e0;
          position: relative;
          font-weight: 600;
          overflow: hidden;
        }

        .hero-cliptext::before {
          content: "Transparent Education";
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            #a259e0,
            #6366f1,
            #8b5cf6,
            #d946ef
          );
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          clip-path: inset(0 100% 0 0);
          animation: clip-reveal 3s ease-in-out infinite;
        }

        @keyframes clip-reveal {
          0% {
            clip-path: inset(0 100% 0 0);
          }
          50% {
            clip-path: inset(0 0 0 0);
          }
          100% {
            clip-path: inset(0 0 0 100%);
          }
        }

        .hero-transparent-education-text {
          color: #a259e0;
          animation: gradient-text 3s ease-in-out infinite,
            pulse-glow 2s ease-in-out infinite alternate;
          background: linear-gradient(
            45deg,
            #a259e0,
            #6366f1,
            #8b5cf6,
            #d946ef,
            #a259e0
          );
          background-size: 300% 300%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-weight: 600;
          text-shadow: 0 0 20px rgba(162, 89, 224, 0.3);
        }

        @keyframes gradient-text {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        @keyframes pulse-glow {
          0% {
            text-shadow: 0 0 20px rgba(162, 89, 224, 0.3);
            transform: scale(1);
          }
          100% {
            text-shadow: 0 0 30px rgba(162, 89, 224, 0.6),
              0 0 40px rgba(162, 89, 224, 0.4);
            transform: scale(1.02);
          }
        }

        .hero-announce {
          position: fixed;
          top: 90px;
          right: 20px;
          z-index: 1000;
        }

        @media screen and (max-width: 767px) {
          .hero-caption h6 {
            font-weight: 500;
          }

          .hero-caption h2 {
            font-size: 36px;
          }
        }

        @media (max-width: 575.98px) {
          .hero-caption h6 {
            font-size: 13px;
            margin-bottom: 15px;
          }

          .hero-caption h2 {
            font-size: 28px;
            margin-top: 15px;
            margin-bottom: 15px;
            line-height: 1.3;
          }

          .hero-caption p {
            font-size: 13px;
            max-width: 100%;
            line-height: 1.5;
          }

          .hero-main-button-red {
            margin-top: 25px;
          }

          .hero-main-button-red a {
            font-size: 12px;
            padding: 10px 25px;
          }

          .hero-announce {
            top: 70px;
            right: 15px;
          }
        }

        @media (min-width: 576px) and (max-width: 767.98px) {
          .hero-caption h6 {
            font-size: 14px;
            margin-bottom: 18px;
          }

          .hero-caption h2 {
            font-size: 32px;
            margin-top: 18px;
            margin-bottom: 18px;
          }

          .hero-caption p {
            font-size: 13px;
            max-width: 500px;
          }

          .hero-main-button-red {
            margin-top: 28px;
          }

          .hero-main-button-red a {
            font-size: 12px;
            padding: 11px 28px;
          }

          .hero-announce {
            top: 80px;
            right: 18px;
          }
        }

        @media (min-width: 768px) and (max-width: 991.98px) {
          .hero-caption h6 {
            font-size: 15px;
          }

          .hero-caption h2 {
            font-size: 36px;
            margin-top: 20px;
            margin-bottom: 20px;
          }

          .hero-caption p {
            font-size: 14px;
            max-width: 570px;
          }

          .hero-main-button-red {
            margin-top: 30px;
          }

          .hero-main-button-red a {
            font-size: 13px;
            padding: 12px 30px;
          }

          .hero-announce {
            top: 85px;
            right: 19px;
          }
        }

        @media (min-width: 992px) and (max-width: 1199.98px) {
          .hero-caption h6 {
            font-size: 15px;
          }

          .hero-caption h2 {
            font-size: 38px;
            margin-top: 20px;
            margin-bottom: 20px;
          }

          .hero-caption p {
            font-size: 14px;
            max-width: 570px;
          }

          .hero-main-button-red a {
            font-size: 13px;
            padding: 12px 32px;
          }
        }

        @media (min-width: 1200px) {
          .hero-caption h6 {
            font-size: 15px;
          }

          .hero-caption h2 {
            font-size: 36px;
            margin-top: 20px;
            margin-bottom: 20px;
          }

          .hero-caption p {
            font-size: 14px;
            max-width: 570px;
          }

          .hero-main-button-red {
            margin-top: 30px;
          }

          .hero-main-button-red a {
            font-size: 13px;
            padding: 12px 30px;
          }

          .hero-announce {
            top: 90px;
            right: 20px;
          }
        }

        @media (min-width: 1400px) {
          .hero-caption h2 {
            font-size: 42px;
          }

          .hero-caption p {
            font-size: 15px;
            max-width: 600px;
          }

          .hero-main-button-red a {
            font-size: 14px;
            padding: 13px 32px;
          }
        }

        @media (max-width: 767px) and (orientation: landscape) {
          .hero-caption h6 {
            font-size: 12px;
            margin-bottom: 10px;
          }

          .hero-caption h2 {
            font-size: 24px;
            margin-top: 10px;
            margin-bottom: 10px;
          }

          .hero-caption p {
            font-size: 12px;
            line-height: 1.4;
          }

          .hero-main-button-red {
            margin-top: 20px;
          }

          .hero-main-button-red a {
            font-size: 11px;
            padding: 8px 20px;
          }

          .hero-announce {
            top: 60px;
            right: 10px;
          }
        }

        @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
          .hero-caption h2 {
            font-size: 32px;
          }

          .hero-caption p {
            font-size: 13px;
            max-width: 500px;
          }

          .hero-main-button-red {
            margin-top: 25px;
          }
        }

        @media (max-width: 374px) {
          .hero-caption h6 {
            font-size: 12px;
          }

          .hero-caption h2 {
            font-size: 24px;
            line-height: 1.2;
          }

          .hero-caption p {
            font-size: 12px;
          }

          .hero-main-button-red a {
            font-size: 11px;
            padding: 9px 22px;
          }
        }

        @media (min-width: 1600px) {
          .hero-caption h2 {
            font-size: 48px;
          }

          .hero-caption p {
            font-size: 16px;
            max-width: 650px;
          }

          .hero-main-button-red a {
            font-size: 15px;
            padding: 14px 35px;
          }
        }
        .banner-badge-top .subtitle {
          font-size: 16px;
          color: #d946ef;
          font-family: var(--font-primary);
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .icon {
          margin-bottom: 8px;
        }
        .icon i {
          font-size: 16px;
        }
        .rating {
          display: flex;
          gap: 4px;
        }
      `}</style>

      <section
        className={`hero-main-banner ${styles.section} ${styles["main-banner"]}`}
        id="top"
        data-section="section1"
      >
        <video
          autoPlay
          muted
          loop
          id="bg-video"
          className={`hero-bg-video ${styles["bg-video"]}`}
        >
          <source src="/MainHome/course.mp4" type="video/mp4" />
        </video>

        <div
          className={`hero-video-overlay ${styles["video-overlay"]} ${styles["header-text"]}`}
        >
          <div className="container-fluid">
            <div className="row">
              <div style={{ marginLeft: "20px" }} className="col-lg-8">
                {showLogoutSuccessModal && (
                  <div
                    className="alert alert-success mt--30 d-flex justify-content-center align-items-center"
                    style={{
                      width: "100%",
                      position: "fixed",
                      top: "20px",
                      left: 0,
                      right: 0,
                      zIndex: 1050,
                    }}
                  >
                    <i className="feather-check-circle me-2"></i>
                    Logged Out Successfully
                  </div>
                )}

                <div className={`hero-caption ${styles.caption}`}>
                  <div className="banner-badge-top">
                    <div className="icon">
                      <div className="rating">
                        <a href="#">
                          <i className="fa fa-star"></i>
                        </a>
                        <a href="#">
                          <i className="fa fa-star"></i>
                        </a>
                        <a href="#">
                          <i className="fa fa-star"></i>
                        </a>
                        <a href="#">
                          <i className="fa fa-star"></i>
                        </a>
                        <a href="#">
                          <i className="fa fa-star"></i>
                        </a>
                      </div>
                    </div>
                    <span className="subtitle">100+ Trusted Customers</span>
                  </div>
                  <h2 className="text-uppercase">
                    Welcome to{" "}
                    <span className={`hero-cliptext ${styles.cliptext}`}>
                      Transparent Education
                    </span>
                  </h2>
                  <p>
                    This is more than an edu meeting provided by{" "}
                    <Link
                      href="https://emdeondigital.com"
                      className={`hero-link-yellow ${styles.linkYellow}`}
                    >
                      Emdeon Learn
                    </Link>{" "}
                    .
                    <ul className={`hero-list ${styles.heroList} mt-3 ps-0`}>
                      <li className="text-white mb-2">
                        <i className="bi bi-check-circle-fill text-warning me-2"></i>
                        More than an LMS — Empowering Learners, Trainers & HR to
                        Learn, Connect & Grow Together.
                      </li>
                      <li className="text-white mb-2">
                        <i className="bi bi-check-circle-fill text-warning me-2"></i>
                        Upskill your workforce, empower your trainers, and
                        engage your learners — all in one place.
                      </li>
                    </ul>
                  </p>
                  <div
                    className={`hero-main-button-red ${styles["main-button-red"]}`}
                  >
                    <div
                      className={`${styles["scroll-to-section"]} d-flex gap-3`}
                    >
                      <Link href="/home">Enter</Link>
                      <Link
                        href="#"
                        style={{ background: "var(--color-primary)" }}
                        onClick={handleLoginOrLogoutClick}
                      >
                        {user ? "LogOut" : "Login"}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="hero-announce">
            <Announce />
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroImageSection;
